
import {
    applyMiddleware,
    createStore,
    StoreEnhancer,
    compose
} from 'redux';
import thunk from 'redux-thunk';
import { batchedSubscribe } from 'redux-batched-subscribe';
import { nextStoreUpdateEnhancer } from './enhancers/NextStoreUpdateEnhancer';
import { runOnceMiddleware } from './middleware/RunOnceMiddleware';
import { loggingMiddleware } from './middleware/LoggingMiddleware';
import { IAction, IDispatch } from './actions/createActionCreator';
import { IFaceted, pack, Facet } from '../models/Facet';
import { IItemCacheState, itemCacheReducer } from './reducers/itemCacheReducer';
import { IItemCacheDispatch, IItemCacheContext } from './actions/ItemCacheActions';
import { resolveItems, supportFacets } from '../operations/getItems';
import { Omit } from '../utilities/Omit';
import { configurationItemKey, configurationItemKeyFacet } from '../facets/ConfigurationFacets';
import { registerPermanentItemDemands } from '../operations/pruneItems';
import { withGraft, graft as defaultGraft } from '../utilities/Graft';

/**
 * @public
 */
export interface IReducer<IState> {
    (state: IState, action: IAction): IState;
}

/**
 * @public
 */
export interface IStore<IState> {
    readonly dispatch: IDispatch<IState>;
    getState(): IState;
    subscribe(subscriber: () => void): () => void;
    replaceReducer(reducer: IReducer<IState>): void;
}

/**
 * @public
 */
export type IItemCacheConfiguration = IFaceted;

/**
 * @public
 */
export interface IItemCacheStore extends IStore<IItemCacheState> {
    readonly dispatch: IItemCacheDispatch;
    dispose(): void;
}

/**
 * @public
 */
export interface IStoreNotifier {
    (notify: () => void): void;
}

/**
 * @public
 */
export interface ICreateItemCacheStoreOptions {
    configuration?: IItemCacheConfiguration;
    createStore?: typeof createStore;
}

/**
 * @internal
 */
export const DEFAULT_CONFIGURATION: IItemCacheConfiguration = pack({}, {});

/**
 * @internal
 */
export const notifierFacet: Facet<IStoreNotifier> = new Facet('storeNotifier');

/**
 * @public
 */
export function createItemCacheStore(options: ICreateItemCacheStoreOptions): IItemCacheStore {
    const {
        configuration = DEFAULT_CONFIGURATION,
        createStore: storeCreator = createStore
    } = options;

    const cacheContext: IItemCacheContext = {
        // This is for legacy compatibility and should be removed in the next major version.
        configuration: configuration
    };

    let timeoutId: number | null = null;

    function defaultNotifier(notify: () => void): void {
        if (timeoutId === null) {
            timeoutId = window.setTimeout(() => {
                timeoutId = null;

                notify();
            }, 0);
        }
    }

    const notifier = notifierFacet.unpack(configuration) || defaultNotifier;

    const finalItemCacheReducer = (state: IItemCacheState, action: IAction): IItemCacheState => {
        return withGraft({ freeze: true })((graft: typeof defaultGraft) => {
            return itemCacheReducer(state, action, { graft });
        });
    };

    const store = storeCreator(
        finalItemCacheReducer,
        compose(
            applyMiddleware(
                loggingMiddleware,
                runOnceMiddleware,
                thunk.withExtraArgument(cacheContext)
            ),
            nextStoreUpdateEnhancer,
            batchedSubscribe(notifier)
        ) as StoreEnhancer<IItemCacheState>) as Omit<IItemCacheStore, 'dispose'>;

    // Prevent requests from accumulating toward configuration.
    store.dispatch(supportFacets({
        keyFacet: configurationItemKeyFacet,
        facets: []
    }));

    store.dispatch(resolveItems({
        items: {
            [configurationItemKey]: configuration
        }
    }));

    store.dispatch(registerPermanentItemDemands({
        items: {
            [configurationItemKey]: {}
        }
    }));

    return {
        ...store,
        dispose: () => {
            // Nothing right now. In the future, this will dispose any middleware
            // that requires lifetime management.
        }
    };
}
